import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { ArrowDown, ChevronLeft, ChevronRight, ExternalLink, MapPin, X } from 'lucide-react';
import type { Event } from '../../types/api';
import { colors, spacing, typography, borderRadius, shadows, breakpoints } from '../../styles/theme';
import { buildEventPath } from '../../utils/slug';
import {
  getEventDateTimeDisplay,
  getEventFormat,
  getEventLocationLabel,
  getVenueLabel,
} from '../../utils/eventPresentation';
import EventBadges from './EventBadges';
import RememberButton from './RememberButton';

const Pane = styled.section`
  position: absolute;
  z-index: 1000;
  right: ${spacing[3]};
  bottom: ${spacing[3]};
  width: min(340px, calc(100% - ${spacing[6]}));
  max-height: calc(100% - ${spacing[6]});
  overflow-y: auto;
  padding: ${spacing[4]};
  border: 1px solid ${colors.border.normal};
  border-radius: ${borderRadius.lg};
  background: ${colors.white};
  box-shadow: ${shadows.lg};

  @media (max-width: ${breakpoints.md}) {
    left: ${spacing[2]};
    right: ${spacing[2]};
    bottom: ${spacing[2]};
    width: auto;
    max-height: 72%;
    border-radius: ${borderRadius.lg} ${borderRadius.lg} 0 0;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${spacing[2]};
`;

const Title = styled.h3`
  margin: 0;
  min-width: 0;
  color: ${colors.text.primary};
  font-size: ${typography.fontSizes.lg};
  line-height: 1.25;
  overflow-wrap: anywhere;
`;

const IconButton = styled.button`
  width: 44px;
  height: 44px;
  flex: 0 0 44px;
  border: 0;
  border-radius: ${borderRadius.md};
  background: transparent;
  color: ${colors.text.secondary};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    background: ${colors.background.secondary};
    color: ${colors.text.primary};
  }
`;

const Meta = styled.div`
  display: grid;
  gap: 4px;
  margin: ${spacing[2]} 0 ${spacing[3]};
  color: ${colors.text.secondary};
  font-size: ${typography.fontSizes.sm};
`;

const MetaLine = styled.span`
  display: inline-flex;
  align-items: flex-start;
  gap: ${spacing[1]};
`;

const Actions = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${spacing[2]};
  margin-top: ${spacing[3]};

  @media (max-width: ${breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const PrimaryAction = styled(Link)`
  min-height: 44px;
  padding: ${spacing[2]} ${spacing[3]};
  border-radius: ${borderRadius.md};
  background: ${colors.primary[600]};
  color: ${colors.white};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing[2]};
  font-size: ${typography.fontSizes.sm};
  font-weight: ${typography.fontWeights.semibold};
  text-decoration: none;
`;

const SecondaryAction = styled.button`
  min-height: 44px;
  padding: ${spacing[2]} ${spacing[3]};
  border: 1px solid ${colors.border.normal};
  border-radius: ${borderRadius.md};
  background: ${colors.white};
  color: ${colors.text.primary};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing[2]};
  font-size: ${typography.fontSizes.sm};
  font-weight: ${typography.fontWeights.semibold};
  cursor: pointer;
`;

const Pager = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing[2]};
  margin-bottom: ${spacing[2]};
  font-size: ${typography.fontSizes.xs};
  color: ${colors.text.secondary};
`;

export interface MapSelectionPaneProps {
  events: Event[];
  activeEventId: number;
  onSelectEvent: (eventId: number) => void;
  onClose: () => void;
  onFindInList: (eventId: number) => void;
  onViewEvent?: (eventId: number) => void;
}

export const MapSelectionPane: React.FC<MapSelectionPaneProps> = ({
  events,
  activeEventId,
  onSelectEvent,
  onClose,
  onFindInList,
  onViewEvent,
}) => {
  const paneRef = useRef<HTMLElement>(null);
  const activeIndex = Math.max(0, events.findIndex(event => event.eventId === activeEventId));
  const event = events[activeIndex] || events[0];

  useEffect(() => {
    paneRef.current?.focus({ preventScroll: true });
  }, []);
  if (!event) return null;

  const when = getEventDateTimeDisplay(event);
  const venue = getVenueLabel(event) || getEventLocationLabel(event);
  const path = buildEventPath(event) || `/event/${event.eventId}`;
  const move = (offset: number) => {
    const next = (activeIndex + offset + events.length) % events.length;
    onSelectEvent(events[next].eventId);
  };

  return (
    <Pane
      ref={paneRef}
      role="dialog"
      aria-label={`Map details for ${event.title}`}
      tabIndex={-1}
      onKeyDown={keyboardEvent => {
        if (keyboardEvent.key === 'Escape') {
          keyboardEvent.stopPropagation();
          onClose();
        }
      }}
    >
      {events.length > 1 && (
        <Pager aria-label={`${events.length} events at this location`}>
          <IconButton type="button" aria-label="Previous event" onClick={() => move(-1)}>
            <ChevronLeft size={18} aria-hidden="true" />
          </IconButton>
          <span>{`${activeIndex + 1} of ${events.length} events here`}</span>
          <IconButton type="button" aria-label="Next event" onClick={() => move(1)}>
            <ChevronRight size={18} aria-hidden="true" />
          </IconButton>
        </Pager>
      )}
      <Header>
        <Title>{event.title}</Title>
        <IconButton type="button" aria-label="Close map details" onClick={onClose}>
          <X size={18} aria-hidden="true" />
        </IconButton>
      </Header>
      <Meta>
        {(when.weekday || when.dateLabel) && (
          <span>{[when.weekday, when.dateLabel].filter(Boolean).join(', ')}</span>
        )}
        {when.timeLabel && <span>{when.timeLabel}</span>}
        <span>{getEventFormat(event)}</span>
        {venue && (
          <MetaLine><MapPin size={15} aria-hidden="true" />{venue}</MetaLine>
        )}
      </Meta>
      <EventBadges event={event} maxRelationships={1} />
      <Actions>
        <PrimaryAction to={path} onClick={() => onViewEvent?.(event.eventId)}>
          View event <ExternalLink size={16} aria-hidden="true" />
        </PrimaryAction>
        <SecondaryAction type="button" onClick={() => onFindInList(event.eventId)}>
          <ArrowDown size={16} aria-hidden="true" /> Find in list
        </SecondaryAction>
        <RememberButton eventId={event.eventId} actionLocation="map_details" />
      </Actions>
    </Pane>
  );
};

export default MapSelectionPane;